import React, { useState, useEffect } from "react";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import LocalPurchaseOrderModal from "./LocalPurchaseOrderModal"; // Import modal
import { buildApiUrl } from "../../../config"; // Adjust if needed
import ConfirmDialog from "../../ui/ConfirmDialog";
import Swal from "sweetalert2";
import LocalPurchaseOrderItemsTable from "./LocalPurchaseOrderItemsTable";
import LocalPurchaseOrderEditModal from "./LocalPurchaseOrderEditModal";
import { FaFilePdf, FaPlus } from "react-icons/fa";
import { formatDate } from "../../ui/formatDate";
import { ColorRing } from "react-loader-spinner";
const LocalPurchaseOrdersTable = () => {
  const navigate = useNavigate();
  const [localOrders, setLocalOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const userRole = localStorage.getItem("role");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const handleEdit = (orderId) => {
    setSelectedOrderId(orderId);
    setIsEditModalOpen(true);
  };
  const toggleRowExpansion = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };
  useEffect(() => {
    fetchLocalPurchaseOrders();
  }, []);

  const fetchLocalPurchaseOrders = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(buildApiUrl("/api/localpurchaseorder/"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setLocalOrders(data);
      } else {
        setError("Invalid response format.");
      }
    } catch (error) {
      setError("An error occurred while fetching local purchase orders.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredOrders = localOrders.filter(
    (order) =>
      order.clientDetails?.firmName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      order.createdBy?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDelete = async (orderId) => {
    const confirm = await ConfirmDialog({
      title: "Are you sure?",
      text: "This action cannot be undone. The Local Purchase Order will be permanently deleted.",
      icon: "warning",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          buildApiUrl(`/api/localpurchaseorder/${orderId}`),
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          setLocalOrders(localOrders.filter((order) => order._id !== orderId));
          Swal.fire({
            title: "Deleted!",
            text: "The Local Purchase Order has been deleted successfully.",
            icon: "success",
            confirmButtonColor: "#4CAF50",
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to delete Local Purchase Order.",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: "An error occurred while deleting the order.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    }
  };

  return (
    <div className="overflow-x-auto bg-white">
      <div className="">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search Local Purchase Orders..."
            className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-700"
          />
          {(userRole === "admin" || userRole === "sales executive") && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-indigo-800"
            >
              <FaPlus size={12} />
              Add Local P.O.
            </button>
          )}
        </div>

        {/* Loading/Error States */}
        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-normal tracking-wide">#</th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Created At
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Estimate
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Firm Name
                  </th>

                  <th className="p-2 text-left font-normal tracking-wide">
                    Total Cost
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Status
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Created By
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Pdf
                  </th>
                  {(userRole === "admin" || userRole === "sales executive") && (
                    <th className="p-2 text-left font-normal tracking-wide">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={20} className="py-2 text-xs">
                      <p className="text-center">No Orders Found!</p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((order, index) => (
                    <React.Fragment key={order._id}>
                      <tr
                        key={order._id}
                        className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                        onClick={() => toggleRowExpansion(order._id)}
                      >
                        <td className="px-2 py-0.5">
                          {index + indexOfFirstItem + 1}
                        </td>
                        <td className="px-2 py-0.5 text-nowrap">
                          {formatDate(order.createdAt).toLocaleString()}
                        </td>
                        <td className="px-2 py-0.5 text-red-700 font-medium">
                          {order.purchaseNo}
                        </td>
                        <td className="px-2 py-0.5 text-nowrap">
                          {order.clientDetails?.firmName}
                        </td>

                        <td className="px-2 py-0.5">
                          ₹{order.totalPurchaseCost}
                        </td>
                        <td className="px-2 py-0.5">
                          <span
                            className={`px-2 py-0.5 rounded-md ${
                              order.status === "open"
                                ? "bg-green-200"
                                : "bg-orange-200"
                            }`}
                          >
                            {order.status.charAt(0).toUpperCase() +
                              order.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-2 py-0.5 text-nowrap">
                          {order.createdBy?.name}
                        </td>
                        <td
                          onClick={() => {
                            navigate(
                              `/purchaseOrder/localpurchaseorder/${order._id}`
                            );
                          }}
                          className="px-2 py-0.5 text-red-600 hover:text-red-700 "
                        >
                          <FaFilePdf size={16} />
                        </td>
                        {(userRole === "admin" ||
                          userRole === "sales executive") && (
                          <td className="px-2 py-0.5 text-sm text-center space-x-2 ">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row expansion when clicking "Edit"
                                  handleEdit(order._id);
                                }}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <HiOutlinePencil size={16} />
                              </button>
                              {userRole === "admin" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row expansion when clicking "Delete"
                                    handleDelete(order._id);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <HiOutlineTrash size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      {expandedOrderId === order._id && (
                        <tr>
                          <td colSpan="20">
                            <LocalPurchaseOrderItemsTable
                              items={order.items}
                              isOpen={true}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, localOrders.length)} of{" "}
            {localOrders.length} orders
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 text-gray-600 rounded-lg"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 text-gray-600 rounded-lg"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <LocalPurchaseOrderModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        fetchLocalPurchaseOrders={fetchLocalPurchaseOrders}
      />
      <LocalPurchaseOrderEditModal
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        orderId={selectedOrderId}
        fetchLocalPurchaseOrders={fetchLocalPurchaseOrders}
      />
      <ToastContainer />
    </div>
  );
};

export default LocalPurchaseOrdersTable;
